import type { IconProps } from "@phosphor-icons/react";
import type { ReactElement } from "react";
import { IconButton } from "@/components/ui/icon-button";
import { heraldPdfExcludeProps } from "@/lib/chat/heraldChatDomDataAttrs";

export interface ChatComponentExportToolbarAction {
  /** List key when multiple actions exist; otherwise index is used. */
  id?: string;
  icon: ReactElement<IconProps>;
  "aria-label": string;
  tooltipLabel: string;
  onPress: () => void;
  isPending?: boolean;
  isDisabled?: boolean;
}

interface ChatComponentExportToolbarProps {
  actions: ChatComponentExportToolbarAction[];
}

/**
 * Floating top-right toolbar that mounts inside a chat catalog wrapper.
 *
 * On hover-capable pointers the toolbar stays hidden until the parent is
 * hovered or a control inside receives focus (`group/chat-export` on the
 * parent). On primary pointers that cannot hover, it stays visible so touch
 * users can reach export actions.
 */
export const ChatComponentExportToolbar = ({
  actions,
}: ChatComponentExportToolbarProps) => (
  <div
    {...heraldPdfExcludeProps}
    className="absolute right-2 top-2 z-10 flex items-center gap-0.5
      rounded-md bg-background/90
      transition-opacity
      [@media(hover:none)]:opacity-100
      [@media(hover:hover)]:opacity-0
      [@media(hover:hover)]:group-hover/chat-export:opacity-100
      [@media(hover:hover)]:focus-within:opacity-100"
  >
    {actions.map(
      (
        {
          id,
          icon,
          "aria-label": ariaLabel,
          tooltipLabel,
          onPress,
          isPending,
          isDisabled,
        },
        index,
      ) => (
        <IconButton
          key={id ?? index}
          icon={icon}
          variant="outline"
          size="md"
          aria-label={ariaLabel}
          tooltipLabel={tooltipLabel}
          tooltipPosition="bottom"
          isPending={isPending}
          isDisabled={isDisabled}
          onPress={onPress}
          className="p-0"
        />
      ),
    )}
  </div>
);
